import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
  FormGroup
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE_URL = '';

function Stops() {
  const { hasPermission } = useAuth();
  
  // Jogosultság ellenőrzés - szerkesztéshez kell a canManageRoutes jogosultság
  const canEdit = hasPermission('canManageRoutes');

  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', isActive: true });
  
  // Import states
  const [importing, setImporting] = useState(false);

  // Megállók lekérdezése
  const fetchStops = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = showInactive ? '/api/stops/all' : '/api/stops';
      const response = await axios.get(`${API_BASE_URL}${endpoint}`);
      setStops(response.data || []);
    } catch (err) {
      console.error('Hiba a megállók lekérdezése során:', err);
      setError('Hiba a megállók lekérdezése során: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStops();
  }, [showInactive]);

  // Sikeres üzenet automatikus eltüntetése
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Szűrt megállók
  const filteredStops = stops.filter(stop => 
    stop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (stop.code && stop.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Dialog kezelés
  const handleOpenDialog = (stop = null) => {
    if (stop) {
      setEditingStop(stop);
      setFormData({ 
        name: stop.name, 
        code: stop.code || '', 
        isActive: stop.isActive 
      });
    } else {
      setEditingStop(null);
      setFormData({ name: '', code: '', isActive: true });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStop(null);
    setFormData({ name: '', code: '', isActive: true });
  };

  // Megálló mentése
  const handleSaveStop = async () => {
    if (!formData.name.trim()) {
      setError('A megálló neve kötelező');
      return;
    }

    try {
      if (editingStop) {
        await axios.put(`${API_BASE_URL}/api/stops/${editingStop.id}`, formData);
        setSuccess('Megálló sikeresen frissítve');
      } else {
        await axios.post(`${API_BASE_URL}/api/stops`, formData);
        setSuccess('Megálló sikeresen létrehozva');
      }
      handleCloseDialog();
      fetchStops();
    } catch (err) {
      setError('Hiba a megálló mentése során: ' + (err.response?.data?.message || err.message));
    }
  };

  // Megálló törlése (deaktiválás)
  const handleDeleteStop = async (id) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt a megállót?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/stops/${id}`);
      setSuccess('Megálló sikeresen törölve');
      fetchStops();
    } catch (err) {
      setError('Hiba a megálló törlése során: ' + (err.response?.data?.message || err.message));
    }
  };

  // Excel import
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/stops/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(response.data.message);
      fetchStops();
    } catch (err) {
      setError('Hiba az importálás során: ' + (err.response?.data?.message || err.message));
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Megállók
      </Typography>

      {/* Eszköztár */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Keresés"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          
          {canEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Új megálló
            </Button>
          )}

          {canEdit && (
            <Button
              variant="contained"
              component="label"
              color="secondary"
              startIcon={importing ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              disabled={importing}
            >
              {importing ? 'Importálás...' : 'Excel import'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
              />
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchStops}
          >
            Frissítés
          </Button>

          <Button
            variant={showInactive ? 'contained' : 'outlined'}
            color={showInactive ? 'warning' : 'inherit'}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Inaktívak mutatása' : 'Csak aktívak'}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredStops.length} megálló
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
      </Paper>

      {/* Megállók táblázat */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Megálló neve</TableCell>
                <TableCell>Kód</TableCell>
                <TableCell>Státusz</TableCell>
                <TableCell>Létrehozva</TableCell>
                <TableCell align="right">Műveletek</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStops.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      Nincs megjeleníthető megálló
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStops.map((stop) => (
                  <TableRow key={stop.id} hover sx={{ opacity: stop.isActive ? 1 : 0.5 }}>
                    <TableCell>{stop.id}</TableCell>
                    <TableCell>{stop.name}</TableCell>
                    <TableCell>{stop.code || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={stop.isActive ? 'Aktív' : 'Inaktív'} 
                        color={stop.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(stop.createdAt).toLocaleDateString('hu-HU')}
                    </TableCell>
                    <TableCell align="right">
                      {canEdit && (
                        <>
                          <Tooltip title="Szerkesztés">
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenDialog(stop)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Törlés">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteStop(stop.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Megálló szerkesztő dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingStop ? 'Megálló szerkesztése' : 'Új megálló'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Megálló neve"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Kód (opcionális)"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={formData.isActive ? "Aktív" : "Inaktív"}
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Mégse</Button>
          <Button onClick={handleSaveStop} variant="contained" color="primary">
            Mentés
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Stops;
